import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Premium Cart Icon
 *
 * Keeps the header cart counter synchronized with Shopify's cart state.
 *
 * Features:
 * - Live cart count updates
 * - Cart bubble animation
 * - BFCache / pageshow restoration
 * - Short-lived sessionStorage synchronization
 * - 99+ display for large quantities
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = [
    'cartBubble',
    'cartBubbleText',
    'cartBubbleCount',
  ];

  static STORAGE_KEY = 'cart-count';
  static STORAGE_TTL = 10000;

  /**
   * Current numeric cart quantity.
   *
   * @returns {number}
   */
  get currentCartCount() {
    const count = Number.parseInt(
      this.refs.cartBubbleCount.dataset.cartCount ??
        this.refs.cartBubbleCount.textContent ??
        '0',
      10
    );

    return Number.isFinite(count) ? count : 0;
  }

  /**
   * Updates the visible cart count.
   *
   * @param {number} value
   */
  set currentCartCount(value) {
    const safeValue = Math.max(0, Math.floor(value));

    /*
     * Keep the real value available even when the visual
     * representation is shortened to "99+".
     */
    this.refs.cartBubbleCount.dataset.cartCount =
      String(safeValue);

    this.refs.cartBubbleCount.textContent =
      safeValue > 99 ? '99+' : String(safeValue);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.addEventListener(
      'pageshow',
      this.onPageShow
    );

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.removeEventListener(
      'pageshow',
      this.onPageShow
    );

    super.disconnectedCallback();
  }

  /**
   * Synchronizes the cart counter when a page is restored
   * from the browser's back/forward cache.
   *
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (!event.persisted) return;

    this.ensureCartBubbleIsCorrect();
  };

  /**
   * Handles Shopify cart updates.
   *
   * @param {CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount =
          cart?.totalQuantity ??
          detail?.itemCount ??
          0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn(
          '[cart-icon] Event promise rejected:',
          error
        );
      });
  };

  /**
   * Updates the visual cart bubble.
   *
   * @param {number} itemCount
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (
    itemCount,
    animate = true
  ) => {
    const safeCount = Math.max(
      0,
      Number.isFinite(Number(itemCount))
        ? Math.floor(Number(itemCount))
        : 0
    );

    const hasItems = safeCount > 0;

    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      !hasItems
    );

    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      !hasItems
    );

    this.currentCartCount = safeCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      hasItems
    );

    this.#saveCartCount(safeCount);

    if (!animate || !hasItems) return;

    /*
     * Wait one frame so the browser paints the visible
     * state before the animation class is applied.
     */
    await new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });

    if (!this.isConnected) return;

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );

    /*
     * Force the class state to settle so repeated cart
     * updates can restart the animation cleanly.
     */
    void this.refs.cartBubble.offsetWidth;

    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );

    await onAnimationEnd(
      this.refs.cartBubbleText
    );

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };

  /**
   * Saves a short-lived cart count snapshot.
   *
   * @param {number} value
   */
  #saveCartCount(value) {
    try {
      sessionStorage.setItem(
        CartIcon.STORAGE_KEY,
        JSON.stringify({
          value,
          timestamp: Date.now(),
        })
      );
    } catch {
      // Storage can be unavailable in restricted browser modes.
    }
  }

  /**
   * Restores a recent cart count when the visible count
   * is stale after browser history restoration.
   */
  ensureCartBubbleIsCorrect = () => {
    let storedValue;

    try {
      storedValue = sessionStorage.getItem(
        CartIcon.STORAGE_KEY
      );
    } catch {
      return;
    }

    if (!storedValue) return;

    try {
      const { value, timestamp } =
        JSON.parse(storedValue);

      const count = Number(value);
      const storedAt = Number(timestamp);

      if (
        !Number.isFinite(count) ||
        count < 0 ||
        !Number.isFinite(storedAt)
      ) {
        return;
      }

      /*
       * Ignore old data.
       */
      if (
        Date.now() - storedAt >
        CartIcon.STORAGE_TTL
      ) {
        return;
      }

      if (count === this.currentCartCount) {
        return;
      }

      this.renderCartBubble(count, false);
    } catch {
      // Ignore malformed storage data.
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define(
    'cart-icon',
    CartIcon
  );
}